import { memo, useMemo, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../../locales/messages";
import { ThreadGroup, ReplyIndent } from "../../styled";
import { CommentCard } from "./CommentCard";
import { ThreadView } from "./useCommentThreads";
import { Author, AuthorResolver } from "./useAuthorResolver";
import { isOpenComment } from "./commentTypes";

interface Props {
  thread: ThreadView;
  resolveAuthor: AuthorResolver;
  resolveResolvedBy: (
    c: ThreadView["root"],
  ) => Author | undefined;
}

const COLLAPSE_AFTER = 2;

export const CommentThread = memo(function CommentThread({
  thread,
  resolveAuthor,
  resolveResolvedBy,
}: Props) {
  const { t } = useTranslation();
  const replyCount = thread.replies.length;

  const hasOpenReply = useMemo(
    () => thread.replies.some((r) => isOpenComment(r.comment)),
    [thread.replies],
  );

  const defaultExpanded = replyCount <= COLLAPSE_AFTER || hasOpenReply;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const repliesToShow = expanded
    ? thread.replies
    : thread.replies.slice(replyCount - COLLAPSE_AFTER);

  const hiddenCount = expanded ? 0 : replyCount - COLLAPSE_AFTER;

  return (
    <ThreadGroup data-testid={`thread-${thread.root.id}`}>
      <CommentCard
        comment={thread.root}
        author={resolveAuthor(thread.root)}
        resolvedBy={resolveResolvedBy(thread.root)}
        variant="root"
      />
      {replyCount > 0 && (
        <ReplyIndent>
          <Stack spacing={0.5}>
            {hiddenCount > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded(true)}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
                data-testid={`thread-expand-${thread.root.id}`}
              >
                {t(messages.Show_N_More_Replies(), { count: hiddenCount })}
              </Button>
            )}
            {repliesToShow.map(({ comment, depth }) => (
              <Box
                key={comment.id}
                sx={{
                  pl: Math.max(0, depth - 1) * 2,
                }}
              >
                <CommentCard
                  comment={comment}
                  author={resolveAuthor(comment)}
                  variant="reply"
                />
              </Box>
            ))}
          </Stack>
        </ReplyIndent>
      )}
    </ThreadGroup>
  );
});
